import React, { useState, useRef } from 'react';
import { FiPlay, FiPause, FiMic } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { motion } from 'framer-motion';

const AiAgentSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const audioRef = useRef(null);

  // Imagem do Avatar no Player
  const agentImage = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=200&h=200";
  
  // Imagem de fundo opaca (Elegância)
  const bgImage = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80&w=1920";

  // Imagem Realista Principal (Fornecida pelo usuário)
  const mainMockupImage = "https://img.magnific.com/fotos-premium/retrato-de-call-center-ou-mulher-feliz-digitando-no-smartphone-no-intervalo-com-sorriso-no-atendimento-ao-cliente-telecom-relaxante-ou-consultor-de-telemarketing-amigavel-em-comunicacao-no-aplicativo-movel-de-midia-social_590464-192520.jpg?semt=ais_hybrid&w=740&q=80";

  const whatsappLink = "https://wa.me/5511999999999?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20a%20cria%C3%A7%C3%A3o%20do%20meu%20app.";

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration;
      setProgress((current / total) * 100);
      setCurrentTime(formatTime(current));
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(formatTime(audioRef.current.duration));
    }
  };

  const handleSeek = (e) => {
    if (audioRef.current) {
      const seekTime = (e.target.value / 100) * audioRef.current.duration;
      audioRef.current.currentTime = seekTime;
      setProgress(e.target.value);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime('0:00');
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  return (
    <section className="py-32 relative overflow-hidden flex items-center justify-center min-h-[80vh] w-full" id="como-funciona">
      
      {/* Wave Top */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none">
        <svg className="relative block w-[calc(100%+1.3px)] h-[50px] sm:h-[90px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-[#f4fcf8]"></path>
        </svg>
      </div>

      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 z-0 opacity-[0.04] dark:opacity-[0.06] bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${bgImage})` }}
      ></div>
      <div className="absolute inset-0 z-0 bg-gray-50/90 dark:bg-[#0f172a]/95"></div>
      
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-center max-w-6xl mx-auto w-full gap-12 lg:gap-20">
          
          {/* Lado Esquerdo: Imagem Principal Realista */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full lg:w-1/2 flex justify-center lg:justify-end"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/5] sm:aspect-[3/4] w-full max-w-sm lg:max-w-md border border-gray-200/50 dark:border-gray-800/50 group">
              <img 
                src={mainMockupImage} 
                alt="Pessoa utilizando aplicativo" 
                className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-105"
              />
              {/* Gradiente sutil para dar profundidade */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>
            </div>
          </motion.div>

          {/* Lado Direito: Separador (Desktop oculto, Mobile visível), Player de Áudio e CTA */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="w-full lg:w-1/2 flex flex-col items-center lg:items-start relative"
          >
            {/* Separador Discreto (Apenas no Mobile) */}
            <div className="w-12 h-1.5 bg-gradient-to-r from-[#25D366]/40 to-transparent rounded-full mb-10 lg:hidden"></div>

            <div className="w-full max-w-md relative mb-10">
              <div className="bg-white dark:bg-[#202c33] rounded-2xl p-5 shadow-xl border border-gray-100 dark:border-gray-800 relative z-20">
                
                {/* Header Simples com WhatsApp Icon */}
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100 dark:border-gray-700/50">
                  <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center text-white shrink-0 shadow-sm">
                    <FaWhatsapp className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-white text-[15px]">Ouça nosso Especialista</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Mensagem de voz</p>
                  </div>
                </div>

                {/* Mensagem de Áudio da IA (Player WhatsApp) */}
                <div className="bg-[#f0f2f5] dark:bg-[#111b21] rounded-xl p-3 max-w-[95%] shadow-inner relative mx-auto lg:mx-0">
                  
                  {/* Tail (Ponta do balão) */}
                  <div className="absolute top-0 -left-2 w-4 h-4 bg-[#f0f2f5] dark:bg-[#111b21] rounded-br-lg" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }}></div>

                  <div className="flex items-center gap-3 w-full">
                    
                    {/* Foto Perfil no Áudio */}
                    <div className="relative shrink-0">
                      <img src={agentImage} alt="Especialista" className="w-12 h-12 rounded-full object-cover shadow-sm border-2 border-white dark:border-[#202c33]" />
                      <div className="absolute -bottom-1 -right-1 text-[#53bdeb] bg-[#f0f2f5] dark:bg-[#111b21] rounded-full p-0.5 border-2 border-white dark:border-[#202c33]">
                        <FiMic className="w-3.5 h-3.5" />
                      </div>
                    </div>

                    {/* Controles do Player */}
                    <div className="flex flex-col flex-1 py-1">
                      <div className="flex items-center gap-3 mb-1">
                        {/* Botão Play/Pause */}
                        <button 
                          onClick={togglePlayPause}
                          className="text-gray-600 dark:text-gray-300 hover:text-[#25D366] dark:hover:text-[#25D366] transition-colors focus:outline-none"
                          aria-label={isPlaying ? "Pausar áudio" : "Tocar áudio"}
                        >
                          {isPlaying ? <FiPause className="w-7 h-7 fill-current" /> : <FiPlay className="w-7 h-7 fill-current" />}
                        </button>
                        
                        {/* Barra de Progresso Customizada */}
                        <div className="relative w-full flex items-center h-5">
                          {/* Elemento de áudio real oculto */}
                          <audio 
                            ref={audioRef}
                            src="/assets/audio/ia-greeting.mp3"
                            onTimeUpdate={handleTimeUpdate}
                            onLoadedMetadata={handleLoadedMetadata}
                            onEnded={handleEnded}
                            onError={(e) => console.error("Erro no audio:", e)}
                            preload="metadata"
                            className="hidden"
                          />
                          
                          <input 
                            type="range" 
                            min="0" 
                            max="100" 
                            value={progress || 0}
                            onChange={handleSeek}
                            className="absolute w-full h-2 opacity-0 cursor-pointer z-20"
                            aria-label="Progresso do áudio"
                          />
                          
                          {/* Fundo da barra */}
                          <div className="w-full h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full absolute z-0 overflow-hidden">
                            {/* Progresso preenchido verde */}
                            <div 
                              className="h-full bg-[#25D366] rounded-full" 
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                          
                          {/* Bolinha (Thumb) */}
                          <div 
                            className="absolute w-3.5 h-3.5 bg-[#25D366] rounded-full z-10 shadow-sm pointer-events-none transition-all duration-75"
                            style={{ left: `calc(${progress}% - 7px)` }}
                          ></div>
                        </div>
                      </div>
                      
                      {/* Tempo e Checks */}
                      <div className="flex justify-between items-center text-[11px] text-gray-500 dark:text-gray-400 pl-10 pr-1">
                        <span className="font-medium">{isPlaying ? currentTime : duration}</span>
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4 text-[#53bdeb]" fill="currentColor" viewBox="0 0 24 24"><path d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z"></path></svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Elemento Decorativo Brilho Atrás do Player */}
              <div className="absolute -inset-4 bg-[#25D366]/5 rounded-3xl blur-xl z-0 pointer-events-none"></div>
            </div>

            {/* Botão CTA WhatsApp */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <a 
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out"></div>
                <FaWhatsapp className="w-6 h-6 relative z-10" />
                <span className="relative z-10 text-lg">Falar com Especialista</span>
              </a>
            </motion.div>

          </motion.div>

        </div>
      </div>

      {/* Wave Bottom */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] z-20 pointer-events-none rotate-180">
        <svg className="relative block w-[calc(100%+1.3px)] h-[50px] sm:h-[90px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-white"></path>
        </svg>
      </div>
    </section>
  );
};

export default AiAgentSection;
